import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { DOCUMENTATION_AREA_ROUTES } from "src/apps/routes/Navigation";
import {
  getDocumentationAreaRemovalReason,
  getUnavailableLocationState,
} from "src/apps/utils/getDocumentationAreaRemovalReason";

// Any query or mutation answering a coded 404 (removed actor or access) shows the unavailable screen
export const useRedirectOnDocumentationAreaRemoval = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const redirect = (error: unknown) => {
      const reason = getDocumentationAreaRemovalReason(error);
      if (!reason) {
        return;
      }
      navigate(
        {
          pathname: DOCUMENTATION_AREA_ROUTES.DOCUMENTATION_AREA_UNAVAILABLE_SCREEN,
          search: location.search,
        },
        { state: getUnavailableLocationState(reason), replace: true },
      );
    };

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      const isActive = event.query.getObserversCount() > 0;
      if (isActive && event.type === "updated" && event.action.type === "error") {
        redirect(event.action.error);
      }
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        redirect(event.action.error);
      }
    });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, navigate, location.search]);
};
